import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, MapPin, Clock, PhoneCall, Send, CheckCircle, MessageSquare } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { usePublicSettings } from '../contexts/PublicSettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useCmsContent } from '../hooks/useCmsContent';
import { parseHighlight } from '../utils/richText';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_IMAGE_FB = "/assets/photos/site-updates/professional-good-one.webp";
const CONTACT_CTA_IMAGE_FB = "/assets/photos/site-updates/modern-studio.webp";

export const Contact = () => {
  const { settings, executeRecaptcha } = usePublicSettings();
  const { t } = useLanguage();
  const cms = useCmsContent('contact');

  const heroBadge      = cms.get('hero', 'badge',  t('contact.hero.badge'));
  const heroTitle      = cms.get('hero', 'title',  t('contact.hero.title'));
  const heroBody       = cms.get('hero', 'body',   t('contact.hero.body'));
  const heroImage      = cms.get('hero', 'image',  CONTACT_IMAGE_FB);
  const ctaCardTitle   = cms.get('cta_card', 'title',   t('contact.ctaCard.title'));
  const ctaCardBody    = cms.get('cta_card', 'body',    t('contact.ctaCard.body'));
  const ctaCardBtn     = cms.get('cta_card', 'button',  t('contact.ctaCard.button'));
  const ctaCardImageRaw = cms.get('cta_card', 'image', CONTACT_CTA_IMAGE_FB);
  const ctaCardImage   = ctaCardImageRaw === CONTACT_IMAGE_FB ? CONTACT_CTA_IMAGE_FB : ctaCardImageRaw;
  const phoneText      = cms.get('contact_info', 'phone_text', t('contact.phoneText'));
  const locationText   = cms.get('contact_info', 'location',  t('contact.locationText'));
  const responseTime   = cms.get('contact_info', 'response_time', t('contact.responseTime'));
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    inquiry_for: 'myself',
    student_age: '',
    group_size: '',
    age_range: '',
    company_name: '',
    team_size: '',
    native_language: '',
    country: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    if (e.target.name === 'inquiry_for') {
      setFormData({
        ...formData,
        inquiry_for: e.target.value,
        student_age: '',
        group_size: '',
        age_range: '',
        company_name: '',
        team_size: ''
      });
      return;
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const recaptchaToken = await executeRecaptcha('contact');
      await axios.post(`${API}/public/contact`, {
        ...formData,
        student_age: formData.student_age ? Number(formData.student_age) : null,
        group_size: formData.group_size ? Number(formData.group_size) : null,
        team_size: formData.team_size ? Number(formData.team_size) : null,
        recaptcha_token: recaptchaToken
      });
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        inquiry_for: 'myself',
        student_age: '',
        group_size: '',
        age_range: '',
        company_name: '',
        team_size: '',
        native_language: '',
        country: '',
        message: ''
      });
    } catch (err) {
      setError(err.response?.data?.detail || t('contact.errorFallback'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactEmail = settings.contact_email || 'franklytalkingenglish@gmail.com';
  const phoneSupportText = phoneText;
  const isIndividualInquiry = formData.inquiry_for === 'myself' || formData.inquiry_for === 'another-person';
  const isGroupInquiry = formData.inquiry_for === 'group';
  const isCompanyInquiry = formData.inquiry_for === 'company';
  const ageLabel = formData.inquiry_for === 'myself' ? t('contact.ageLabelSelf') : t('contact.ageLabelStudent');
  const nativeLanguageLabel = isCompanyInquiry
    ? t('contact.nativeLanguageLabelTeam')
    : isGroupInquiry
      ? t('contact.nativeLanguageLabelGroup')
      : t('contact.nativeLanguageLabelDefault');
  const countryLabel = isCompanyInquiry ? t('contact.countryLabelCompany') : t('contact.countryLabelDefault');
  const messagePlaceholder = isCompanyInquiry
    ? t('contact.messagePlaceholderCompany')
    : isGroupInquiry
      ? t('contact.messagePlaceholderGroup')
      : t('contact.messagePlaceholderDefault');

  const contactInfo = [
    {
      icon: Mail,
      title: t('contact.infoEmailTitle'),
      content: contactEmail,
      link: `mailto:${contactEmail}`,
      color: 'text-[#D72B2B]',
      bg: 'bg-[#D72B2B]/10'
    },
    {
      icon: PhoneCall,
      title: t('contact.infoPhoneTitle'),
      content: phoneSupportText,
      link: null,
      color: 'text-[#1A2E6E]',
      bg: 'bg-[#1A2E6E]/10'
    },
    {
      icon: MapPin,
      title: t('contact.infoLocationTitle'),
      content: locationText,
      link: null,
      color: 'text-[#4CAF50]',
      bg: 'bg-[#4CAF50]/10'
    },
    {
      icon: Clock,
      title: t('contact.infoResponseTimeTitle'),
      content: responseTime,
      link: null,
      color: 'text-[#F5A623]',
      bg: 'bg-[#F5A623]/15'
    }
  ];

  return (
    <main className="pt-24" data-testid="contact-page">
      {/* Hero Section */}
      <section className="section-padding bg-cream-gradient">
        <div className="container-custom">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.72fr)]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl text-center lg:text-left"
            >
              <span className="badge-navy mb-4 inline-block">
                {heroBadge}
              </span>
              <h1 className="font-sora text-4xl md:text-5xl font-bold text-[#1A2E6E] mb-6">
                {parseHighlight(heroTitle)}
              </h1>
              <p className="text-lg text-[#1A2E6E]/70 leading-relaxed">
                {heroBody.split('\n').map((line, i) => (
                  <React.Fragment key={i}>{line}{i < heroBody.split('\n').length - 1 && <br />}</React.Fragment>
                ))}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="mx-auto w-full max-w-md lg:max-w-none"
            >
              <div className="photo-container h-56 rounded-2xl sm:h-72">
                <img
                  src={heroImage}
                  alt="English lesson support"
                  className="photo-fit-card"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding bg-warm-texture">
        <div className="container-custom">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12 lg:items-stretch">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:h-full"
            >
              <div className="glass-card p-6 sm:p-8 border-t-4 border-t-[#1A2E6E] lg:h-full lg:flex lg:flex-col">
                {isSubmitted ? (
                  <div className="text-center py-12 lg:flex lg:flex-1 lg:flex-col lg:justify-center">
                    <div className="w-16 h-16 rounded-full bg-[#4CAF50]/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle size={32} className="text-[#4CAF50]" />
                    </div>
                    <h3 className="font-sora font-bold text-2xl text-[#1A2E6E] mb-2">
                      {t('contact.successTitle')}
                    </h3>
                    <p className="text-[#1A2E6E]/70">
                      {t('contact.successBody')}
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="lg:h-full lg:flex lg:flex-col" data-testid="contact-form">
                    <div className="space-y-6">
                      <div>
                        <Label htmlFor="name" className="text-[#1A2E6E] font-medium mb-2 block">
                          {t('contact.nameLabel')}
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10"
                          placeholder={t('contact.namePlaceholder')}
                          data-testid="contact-name-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-[#1A2E6E] font-medium mb-2 block">
                          {t('contact.emailLabel')}
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10"
                          placeholder={t('contact.emailPlaceholder')}
                          data-testid="contact-email-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="phone" className="text-[#1A2E6E] font-medium mb-2 block">
                          {t('contact.phoneLabel')}
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10"
                          placeholder={t('contact.phonePlaceholder')}
                          data-testid="contact-phone-input"
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="inquiry_for" className="text-[#1A2E6E] font-medium mb-2 block">
                            {t('contact.inquiryForLabel')}
                          </Label>
                          <select
                            id="inquiry_for"
                            name="inquiry_for"
                            value={formData.inquiry_for}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10 outline-none"
                            data-testid="contact-inquiry-for-input"
                          >
                            <option value="myself">{t('contact.inquiryOptionMyself')}</option>
                            <option value="another-person">{t('contact.inquiryOptionAnotherPerson')}</option>
                            <option value="group">{t('contact.inquiryOptionGroup')}</option>
                            <option value="company">{t('contact.inquiryOptionCompany')}</option>
                          </select>
                        </div>

                        {isIndividualInquiry && (
                          <div>
                            <Label htmlFor="student_age" className="text-[#1A2E6E] font-medium mb-2 block">
                              {ageLabel}
                            </Label>
                            <Input
                              id="student_age"
                              name="student_age"
                              type="number"
                              min="1"
                              value={formData.student_age}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10"
                              placeholder={t('contact.agePlaceholder')}
                              data-testid="contact-student-age-input"
                            />
                          </div>
                        )}

                        {isGroupInquiry && (
                          <div>
                            <Label htmlFor="group_size" className="text-[#1A2E6E] font-medium mb-2 block">
                              {t('contact.groupSizeLabel')}
                            </Label>
                            <Input
                              id="group_size"
                              name="group_size"
                              type="number"
                              min="2"
                              value={formData.group_size}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10"
                              placeholder={t('contact.groupSizePlaceholder')}
                              data-testid="contact-group-size-input"
                            />
                          </div>
                        )}

                        {isCompanyInquiry && (
                          <div>
                            <Label htmlFor="company_name" className="text-[#1A2E6E] font-medium mb-2 block">
                              {t('contact.companyNameLabel')}
                            </Label>
                            <Input
                              id="company_name"
                              name="company_name"
                              type="text"
                              value={formData.company_name}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10"
                              placeholder={t('contact.companyNamePlaceholder')}
                              data-testid="contact-company-name-input"
                            />
                          </div>
                        )}
                      </div>

                      {isGroupInquiry && (
                        <div>
                          <Label htmlFor="age_range" className="text-[#1A2E6E] font-medium mb-2 block">
                            {t('contact.ageRangeLabel')}
                          </Label>
                          <Input
                            id="age_range"
                            name="age_range"
                            type="text"
                            value={formData.age_range}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10"
                            placeholder={t('contact.ageRangePlaceholder')}
                            data-testid="contact-age-range-input"
                          />
                        </div>
                      )}

                      {isCompanyInquiry && (
                        <div>
                          <Label htmlFor="team_size" className="text-[#1A2E6E] font-medium mb-2 block">
                            {t('contact.teamSizeLabel')}
                          </Label>
                          <Input
                            id="team_size"
                            name="team_size"
                            type="number"
                            min="2"
                            value={formData.team_size}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10"
                            placeholder={t('contact.teamSizePlaceholder')}
                            data-testid="contact-team-size-input"
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="native_language" className="text-[#1A2E6E] font-medium mb-2 block">
                            {nativeLanguageLabel}
                          </Label>
                          <Input
                            id="native_language"
                            name="native_language"
                            type="text"
                            value={formData.native_language}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10"
                            placeholder={t('contact.nativeLanguagePlaceholder')}
                            data-testid="contact-native-language-input"
                          />
                        </div>

                        <div>
                          <Label htmlFor="country" className="text-[#1A2E6E] font-medium mb-2 block">
                            {countryLabel}
                          </Label>
                          <Input
                            id="country"
                            name="country"
                            type="text"
                            value={formData.country}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10"
                            placeholder={t('contact.countryPlaceholder')}
                            data-testid="contact-country-input"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="message" className="text-[#1A2E6E] font-medium mb-2 block">
                          {t('contact.messageLabel')}
                        </Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          className="w-full px-4 py-3 rounded-xl border border-[#1A2E6E]/15 bg-white focus:border-[#1A2E6E] focus:ring-4 focus:ring-[#1A2E6E]/10 resize-none"
                          placeholder={messagePlaceholder}
                          data-testid="contact-message-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-4 pt-6 lg:mt-auto">
                      {error && (
                        <p className="text-[#D72B2B] text-sm">{error}</p>
                      )}

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="contact-submit-btn"
                      >
                        {isSubmitting ? (
                          t('contact.sending')
                        ) : (
                          <>
                            {t('contact.sendButton')} <Send size={18} />
                          </>
                        )}
                      </button>

                      <div className="rounded-xl border border-[#1A2E6E]/10 bg-[#1A2E6E]/5 p-4 text-sm leading-relaxed text-[#1A2E6E]/75">
                        {t('contact.consentPrefix')}{' '}
                        <Link to="/privacy" className="font-medium text-[#1A2E6E] underline decoration-[#1A2E6E]/30 underline-offset-4 hover:text-[#D72B2B]">
                          {t('footer.privacyPolicy')}
                        </Link>{' '}
                        {t('contact.consentAnd')}{' '}
                        <Link to="/terms" className="font-medium text-[#1A2E6E] underline decoration-[#1A2E6E]/30 underline-offset-4 hover:text-[#D72B2B]">
                          {t('footer.termsOfService')}
                        </Link>
                        .
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6 lg:h-full lg:flex lg:flex-col lg:gap-6 lg:space-y-0"
            >
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="glass-card p-5 sm:p-6 flex items-start gap-4"
                >
                  <div className={`w-12 h-12 rounded-xl ${info.bg} flex items-center justify-center flex-shrink-0`}>
                    <info.icon size={24} className={info.color} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-sora font-semibold text-[#1A2E6E] mb-1">
                      {info.title}
                    </h3>
                    {info.link ? (
                      <a 
                        href={info.link}
                        className="break-words text-[#1A2E6E]/70 hover:text-[#1A2E6E] transition-colors"
                      >
                        {info.content}
                      </a>
                    ) : (
                      <p className="break-words text-[#1A2E6E]/70">{info.content}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* CTA Card */}
              <div className="relative overflow-hidden rounded-2xl text-white lg:mt-auto">
                <div
                  className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-[#1A2E6E]"
                  style={{ backgroundImage: `url(${ctaCardImage})` }}
                  aria-hidden="true"
                />
                <div
                  className="absolute inset-0 bg-gradient-to-br from-[#1A2E6E]/90 via-[#1A2E6E]/82 to-[#2A4A8E]/88"
                  aria-hidden="true"
                />
                <div className="relative p-6 sm:p-8">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                    <MessageSquare size={24} />
                  </div>
                  <h3 className="font-sora font-bold text-xl text-white mb-3">
                    {parseHighlight(ctaCardTitle)}
                  </h3>
                  <p className="text-white/85 mb-6 max-w-md">
                    {ctaCardBody}
                  </p>
                  <Link
                    to="/book-trial"
                    className="inline-block w-full rounded-full bg-[#D72B2B] px-6 py-3 text-center font-semibold text-white transition-all hover:bg-[#B71C1C] hover:shadow-lg sm:w-auto"
                    data-testid="contact-book-trial-btn"
                  >
                    {ctaCardBtn}
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};
